import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MapContainer, TileLayer, GeoJSON, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Replace direct Firebase imports with context
import { useFirebase } from '../contexts/FirebaseContext';

import { useUser } from '../contexts/UserContext';
import { authErrorMessage } from '../auth/errors';
import AccountRecovery, { AuthLoading } from './AccountRecovery';


// Fix Leaflet marker icons (keeping original code)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Keep all the original map styles (stateStyle, countyStyle, etc.)
const stateStyle = {
  weight: 2.5,
  opacity: 1,
  color: '#444',
  dashArray: '3',
  fillOpacity: 0,
  fillColor: '#ffffff'
};

const countyStyle = {
  weight: 2.5,
  opacity: 1,
  color: '#000000',
  fillOpacity: 0.1,
  fillColor: '#ffffff',
  zIndex: 1000
};

const countyHoverStyle = {
  weight: 3.5,
  color: '#0066cc',
  dashArray: '',
  fillOpacity: 0.3,
  fillColor: '#4dabf7',
  zIndex: 1001
};

const highlightStyle = {
  weight: 4,
  color: '#0066cc',
  dashArray: '',
  fillOpacity: 0.3,
  fillColor: '#0d6efd',
  zIndex: 999
};

const selectedStateStyle = {
  weight: 3,
  color: '#0d6efd',
  dashArray: '',
  fillOpacity: 0,
  fillColor: '#ffffff'
};

const selectedCountyStyle = {
  weight: 3.5,
  color: '#198754',
  dashArray: '',
  fillOpacity: 0.4,
  fillColor: '#198754',
  zIndex: 1002
};

// Keep the original getStateFips function
const getStateFips = (stateName) => {
  const stateToFips = {
    'Alabama': '01', 'Alaska': '02', 'Arizona': '04', 'Arkansas': '05',
    'California': '06', 'Colorado': '08', 'Connecticut': '09', 'Delaware': '10',
    'Florida': '12', 'Georgia': '13', 'Hawaii': '15', 'Idaho': '16',
    'Illinois': '17', 'Indiana': '18', 'Iowa': '19', 'Kansas': '20',
    'Kentucky': '21', 'Louisiana': '22', 'Maine': '23', 'Maryland': '24',
    'Massachusetts': '25', 'Michigan': '26', 'Minnesota': '27', 'Mississippi': '28',
    'Missouri': '29', 'Montana': '30', 'Nebraska': '31', 'Nevada': '32',
    'New Hampshire': '33', 'New Jersey': '34', 'New Mexico': '35', 'New York': '36',
    'North Carolina': '37', 'North Dakota': '38', 'Ohio': '39', 'Oklahoma': '40',
    'Oregon': '41', 'Pennsylvania': '42', 'Rhode Island': '44', 'South Carolina': '45',
    'South Dakota': '46', 'Tennessee': '47', 'Texas': '48', 'Utah': '49',
    'Vermont': '50', 'Virginia': '51', 'Washington': '53', 'West Virginia': '54',
    'Wisconsin': '55', 'Wyoming': '56', 'District of Columbia': '11'
  };

  return stateToFips[stateName] || '';
};

// Add the new function HERE (right after the getStateFips closing brace)
const getFipsToState = (fipsCode) => {
  const fipsToState = {
    '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas',
    '06': 'California', '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware',
    '11': 'District of Columbia', '12': 'Florida', '13': 'Georgia', '15': 'Hawaii',
    '16': 'Idaho', '17': 'Illinois', '18': 'Indiana', '19': 'Iowa',
    '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana', '23': 'Maine',
    '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota',
    '28': 'Mississippi', '29': 'Missouri', '30': 'Montana', '31': 'Nebraska',
    '32': 'Nevada', '33': 'New Hampshire', '34': 'New Jersey', '35': 'New Mexico',
    '36': 'New York', '37': 'North Carolina', '38': 'North Dakota', '39': 'Ohio',
    '40': 'Oklahoma', '41': 'Oregon', '42': 'Pennsylvania', '44': 'Rhode Island',
    '45': 'South Carolina', '46': 'South Dakota', '47': 'Tennessee', '48': 'Texas',
    '49': 'Utah', '50': 'Vermont', '51': 'Virginia', '53': 'Washington',
    '54': 'West Virginia', '55': 'Wisconsin', '56': 'Wyoming'
  };
  
  return fipsToState[fipsCode] || 'Unknown State';
};

// Preserve the AdminBoundaryMap component as is
const AdminBoundaryMap = ({ onCountySelect }) => {
  // ... Original component code preserved - skipping for brevity ...
  // Original AdminBoundaryMap implementation preserved
  const map = useMap();
  const [statesData, setStatesData] = useState(null);
  const [countiesData, setCountiesData] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [stateFeatures, setStateFeatures] = useState({});
  const [countyFeatures, setCountyFeatures] = useState({});
  const [countyLabels, setCountyLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateLocked, setStateLocked] = useState(false);

  // Keep all the original functions for the map
  const clearCountyLabels = () => {
    if (!map) return;
    
    map.eachLayer(layer => {
      if (layer._isCountyLabel) {
        map.removeLayer(layer);
      }
    });
  };

  const updateCountyLabels = () => {
    if (!map) return;
    const currentZoom = map.getZoom();
    
    clearCountyLabels();
    
    if (currentZoom >= 7 && countyLabels.length > 0) {
      countyLabels.forEach(labelInfo => {
        const label = L.marker(labelInfo.center, {
          icon: L.divIcon({
            className: 'county-label',
            html: `<div>${labelInfo.name}</div>`,
            iconSize: [120, 20],
            iconAnchor: [60, 10]
          }),
          interactive: false,
          keyboard: false
        });
        
        label._isCountyLabel = true;
        label.addTo(map);
      });
    }
  };

  const findLayerByStateName = (stateName) => {
    if (!map) return null;
    
    let foundLayer = null;
    map.eachLayer(layer => {
      if (layer.feature && 
          layer.feature.properties && 
          layer.feature.properties.name === stateName) {
        foundLayer = layer;
      }
    });
    return foundLayer;
  };

  const setupStateFeature = (feature, layer) => {
    const stateName = feature.properties.name;
    
    layer.setStyle({ zIndex: 500 });
    
    layer.getElement?.() && layer.getElement().classList.add('state-feature');
    
    layer.on({
      mouseover: (e) => {
        if (!stateLocked) {
          if (!layer.feature || !layer.feature.properties) {
            return;
          }
          
          if (selectedState && layer.feature.properties && 
              layer.feature.properties.name === selectedState) {
            return;
          }
          
          layer.setStyle(highlightStyle);
          
          if (e.originalEvent) {
            L.DomEvent.stopPropagation(e.originalEvent);
          }
        }
      },
      mouseout: (e) => {
        if (!stateLocked) {
          if (!layer.feature || !layer.feature.properties) {
            return;
          }
          
          if (selectedState && layer.feature.properties && 
              layer.feature.properties.name === selectedState) {
            return;
          }
          
          layer.setStyle(stateStyle);
          
          if (e.originalEvent) {
            L.DomEvent.stopPropagation(e.originalEvent);
          }
        }
      },
      click: (e) => {
        if (stateLocked && selectedCounty) {
          if (e.originalEvent && !e.originalEvent._clickedOnCounty) {
            e.originalEvent.preventDefault();
            e.originalEvent.stopPropagation();
            return false;
          }
        }
        
        handleStateClick(e, layer, stateName);
      }
    });
    
    layer.bindTooltip(stateName, {
      permanent: false,
      direction: 'center',
      className: 'state-tooltip'
    });
  };

  const handleStateClick = (e, layer, stateName) => {
  
  if (!map) return;
  
  if (stateLocked) {
    return;
  }
  
    const stateFips = getStateFips(stateName);
    
    setSelectedCounty(null);
    
    layer.setStyle(selectedStateStyle);
    
    if (selectedState && selectedState !== stateName) {
      const stateLayer = findLayerByStateName(selectedState);
      if (stateLayer) {
        stateLayer.setStyle(stateStyle);
      }
    }
    
    setSelectedState(stateName);
    
    clearCountyLabels();
    setCountyLabels([]);
    
    setTimeout(() => {
      if (!map) return;
      map.invalidateSize(true);
      
      map.fitBounds(layer.getBounds(), {animate: false});
      
      map._onResize();
    }, 100);
    
    if (stateFips && countyFeatures[stateFips]) {
      const stateCounties = {
        type: "FeatureCollection",
        features: countyFeatures[stateFips]
      };
      
      map.eachLayer(layer => {
        if (layer._countyLayer) {
          map.removeLayer(layer);
        }
      });
      
      setTimeout(() => {
        if (!map) return;
        
        const countyLayer = L.geoJSON(stateCounties, {
  style: countyStyle,
  onEachFeature: setupCountyFeature,
  interactive: true,
  bubblingMouseEvents: false,
  pane: 'overlayPane'
});

countyLayer._countyLayer = true;
countyLayer.addTo(map);

// ADD THESE FIXES:
countyLayer.bringToFront();

// Force proper layer rendering
setTimeout(() => {
  if (map) {
    map.invalidateSize();
    // Ensure county layer stays on top
    countyLayer.bringToFront();
    // Force redraw of all layers
    map.eachLayer((layer) => {
      if (layer.redraw) {
        layer.redraw();
      }
    });
  }
}, 100);
        
        map.off('zoomend', updateCountyLabels);
        map.on('zoomend', updateCountyLabels);
        
        updateCountyLabels();
        
        document.querySelectorAll('.county-feature').forEach(element => {
          element.addEventListener('click', (e) => {
            e.stopPropagation();
            setStateLocked(true);
          }, true);
        });
      }, 150);
    }
  };

  const setupCountyFeature = (feature, layer) => {
  // Better county name extraction with validation
  let countyName = feature.properties.name || 
                   feature.properties.NAME || 
                   feature.properties.NAMELSAD || 
                   feature.properties.NAME_LONG || 
                   'Unknown County';
  
  // Clean up county name (remove "County" suffix if present)
  countyName = countyName.replace(/ County$/i, '').trim();
  
  // Validate FIPS code
  if (!feature.id || feature.id.length !== 5) {
    console.warn("Invalid county FIPS code:", feature.id);
    return;
  }
  
  const stateFips = feature.id.substring(0, 2);
  const stateName = getFipsToState(stateFips);
  
  if (stateName === 'Unknown State') {
    console.warn("Unknown state for FIPS:", stateFips, "County:", countyName);
  }
    
    layer.setStyle({ zIndex: 1000 });
    
    
    const popup = L.popup({
      closeButton: false,
      offset: L.point(0, -10),
      className: 'county-popup',
      autoPan: false
    }).setContent(`<div class="font-bold text-sm">${countyName}</div>`);
    
    layer.on({
      mouseover: (e) => {
        if (e.originalEvent) {
          e.originalEvent.stopPropagation();
          e.originalEvent.preventDefault();
        }
        
        layer.setStyle(countyHoverStyle);
        
        layer.bindPopup(popup).openPopup();
        
        layer.getElement?.() && (layer.getElement().style.cursor = 'pointer');
      },
      mouseout: (e) => {
        if (e.originalEvent) {
          e.originalEvent.stopPropagation();
          e.originalEvent.preventDefault();
        }
        
        if (!selectedCounty || selectedCounty !== feature.id) {
          layer.setStyle(countyStyle);
        }
        
        layer.closePopup();
        
        layer.getElement?.() && (layer.getElement().style.cursor = '');
      },
      click: (e) => {
        handleCountyClick(e, feature, layer, countyName);
      }
    });
    
    const bounds = layer.getBounds();
    const center = bounds.getCenter();
    
    const labelInfo = {
      center: center,
      name: countyName,
      layer: layer,
      featureId: feature.id
    };
    
    setCountyLabels(prev => [...prev, labelInfo]);
    
    if (!layer._labelInfo) {
      layer._labelInfo = labelInfo;
    }
  };

const handleCountyClick = (e, feature, layer, countyName) => {
  if (e.originalEvent) {
    e.originalEvent.stopPropagation();
    e.originalEvent.preventDefault();
    e.originalEvent.stopImmediatePropagation();
    e.originalEvent._clickedOnCounty = true;
  }
  
  if (!map || !feature || !feature.id) {
    return false;
  }
  
  // Extract state FIPS from county FIPS (first 2 digits)
  const stateFips = feature.id ? feature.id.substring(0, 2) : '';
  const correctStateName = getFipsToState(stateFips);
  
  
  // Update the selected state to match the county
  if (correctStateName !== 'Unknown State') {
    setSelectedState(correctStateName);
  }
  
  map.eachLayer(mapLayer => {
    if (mapLayer.feature && 
        mapLayer.feature.id && 
        mapLayer.feature.id !== feature.id && 
        mapLayer.feature.id.length > 2) {
      mapLayer.setStyle(countyStyle);
    }
  });
  
  layer.setStyle(selectedCountyStyle);
  
  setStateLocked(true);
  setSelectedCounty(feature.id);
  
  // Use the correct state name from FIPS lookup
  onCountySelect({
    county: countyName,
    state: correctStateName,
    fips: feature.id,
    country: "United States"
  });
  
  return false;
};

  const handleBackToStates = () => {
    if (!map) return;
    setSelectedState(null);
    setSelectedCounty(null);
    setStateLocked(false);
    
    map.eachLayer(layer => {
      if (layer._countyLayer) {
        map.removeLayer(layer);
      }
    });
    
    clearCountyLabels();
    
    setCountyLabels([]);
    
  map.eachLayer(layer => {
    if (layer.feature && layer.feature.properties && layer.feature.properties.name) {
      layer.setStyle(stateStyle);
      // ADD THIS: Force immediate redraw
      if (layer.redraw) {
        layer.redraw();
      }
    }
  });
    
    map.invalidateSize(true);
    
setTimeout(() => {
  if (!map) return;
  map.setView([39.8, -98.5], 4, {animate: false});
  
  map._onResize();
  
  const container = map.getContainer();
  if (container) {
    const currentWidth = container.offsetWidth;
    const currentHeight = container.offsetHeight;
    container.style.width = `${currentWidth}px`;
    container.style.height = `${currentHeight}px`;
    map.invalidateSize(true);
  }
  
  // Force redraw all remaining layers
    map.eachLayer((layer) => {
      if (layer.feature && layer.feature.properties && layer.feature.properties.name) {
        layer.setStyle(stateStyle);
      }
      if (layer.redraw) {
        layer.redraw();
      }
      if (layer.bringToFront && layer.feature && layer.feature.properties && layer.feature.properties.name) {
        layer.bringToFront();
      }
    });
    
    // ADD THIS: Final refresh after a short delay
    setTimeout(() => {
      if (map) {
        map.invalidateSize(true);
      }
    }, 50);
  }, 100);
};

useEffect(() => {
  const fetchStatesData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json');
      const data = await response.json();
      setStatesData(data);
      
      const features = {};
      data.features.forEach(feature => {
        features[feature.properties.name] = feature;
      });
      setStateFeatures(features);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading states data:', error);
      setLoading(false);
    }
  };
  fetchStatesData();
  
  if (map) {
    map.on('moveend', () => {
      if (selectedState) {
        updateCountyLabels();
      }
      // ADD THIS: Force layer refresh on move
      setTimeout(() => {
        map.invalidateSize();
      }, 50);
    });
  }
  
  return () => {
    if (map) {
      map.off('moveend');
      map.off('zoomend', updateCountyLabels);
    }
  };
}, [map, selectedState]);

  useEffect(() => {
    const fetchCountiesData = async () => {
      try {
        const response = await fetch('https://raw.githubusercontent.com/plotly/datasets/master/geojson-counties-fips.json');
        const data = await response.json();
        setCountiesData(data);
        
        const features = {};
        data.features.forEach(feature => {
          const stateFips = feature.id.substring(0, 2);
          if (!features[stateFips]) {
            features[stateFips] = [];
          }
          features[stateFips].push(feature);
        });
        setCountyFeatures(features);
        
      } catch (error) {
      }
    };

    fetchCountiesData();
  }, []);

useEffect(() => {
  if (map) {
    const handleZoomEnd = () => {
      const currentZoom = map.getZoom();
      
      setTimeout(() => {
        map.invalidateSize();
        // First, properly handle county layer visibility
        map.eachLayer((layer) => {
          if (layer._countyLayer) {
            if (currentZoom < 6) {
              // Hide counties when zoomed out
              layer.setStyle({ opacity: 0, fillOpacity: 0 });
            } else {
              // Show counties when zoomed in
              layer.setStyle(countyStyle);
              layer.bringToFront();
            }
          }
        });
        
        // CRITICAL: Reset and redraw ALL state layers with proper styling
        map.eachLayer((layer) => {
          if (layer.feature && layer.feature.properties && layer.feature.properties.name) {
            // This is a state layer - reset its style completely
            const stateName = layer.feature.properties.name;
            
            if (selectedState === stateName) {
              layer.setStyle(selectedStateStyle);
            } else {
              layer.setStyle(stateStyle);
            }
            
            // Force the layer to redraw completely
            if (layer.redraw) {
              layer.redraw();
            }
            
            // Ensure state layers are at correct z-index (behind counties when counties are visible)
            if (currentZoom < 6 || !selectedState) {
              layer.bringToFront();
            } else {
              layer.bringToBack();
            }
          }
        });
        
        // Force a complete map refresh
        setTimeout(() => {
          if (map) {
            map.invalidateSize(true);
            
            // Additional layer refresh
            map.eachLayer((layer) => {
              if (layer.feature && layer.feature.properties && layer.feature.properties.name) {
                // Re-apply styles one more time to ensure they stick
                const stateName = layer.feature.properties.name;
                if (selectedState === stateName) {
                  layer.setStyle(selectedStateStyle);
                } else {
                  layer.setStyle(stateStyle);
                }
              }
            });
          }
        }, 50);
        
        // Update county labels
        if (selectedState) {
          updateCountyLabels();
        }
      }, 100);
    };

    const handleMoveEnd = () => {
      setTimeout(() => {
        map.invalidateSize();
        
        // Also refresh state layers on move to prevent rendering issues
        map.eachLayer((layer) => {
          if (layer.feature && layer.feature.properties && layer.feature.properties.name) {
            const stateName = layer.feature.properties.name;
            if (selectedState === stateName) {
              layer.setStyle(selectedStateStyle);
            } else {
              layer.setStyle(stateStyle);
            }
          }
        });
        
        if (selectedState) {
          updateCountyLabels();
        }
      }, 50);
    };

    map.on('zoomend', handleZoomEnd);
    map.on('moveend', handleMoveEnd);
    // Run initial zoom check
    handleZoomEnd();

    return () => {
      map.off('zoomend', handleZoomEnd);
      map.off('moveend', handleMoveEnd);
    };
  }
}, [map, selectedState]);

  useEffect(() => {
    if (map && selectedCounty) {
      map.eachLayer(layer => {
        if (layer.feature && 
            layer.feature.id && 
            layer.feature.id === selectedCounty) {
          layer.setStyle(selectedCountyStyle);
          layer.bringToFront();
          setStateLocked(true);
        }
      });
    }
  }, [map, selectedCounty]);

  return (
    <>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-50">
          <div className="text-blue-600 font-semibold">Loading map data...</div>
        </div>
      )}
      
      {selectedState && (
        <div className="absolute top-2 left-2 z-400 bg-white px-2 py-1 rounded shadow-sm">
          <button 
            onClick={handleBackToStates}
            className="text-sm text-blue-600 flex items-center"
          >
            <span className="mr-1">←</span> Back to States
          </button>
        </div>
      )}
      
      {statesData && !loading && (
        <GeoJSON 
          data={statesData}
          style={stateStyle}
          onEachFeature={setupStateFeature}
        />
      )}
    </>
  );
};

// Keep original MultiSelectDropdown component
const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleOption = (option) => {
    let newSelection;
    
    if (selectedValues.includes(option.value)) {
      newSelection = selectedValues.filter(item => item !== option.value);
    } else {
      newSelection = [...selectedValues, option.value];
    }
    
    onChange(newSelection);
  };
  
  return (
    <div className="relative">
      <div 
        className="shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline cursor-pointer flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate">
          {selectedValues.length > 0 
            ? selectedValues.map(val => options.find(opt => opt.value === val)?.label).join(', ') 
            : placeholder || 'Select options'}
        </span>
        <span className="text-gray-400">{isOpen ? '▲' : '▼'}</span>
      </div>
      
      {isOpen && (
        <div className="absolute z-10 w-full bg-white border border-gray-300 mt-1 rounded max-h-60 overflow-y-auto">
          {options.map(option => (
            <div 
              key={option.value} 
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${selectedValues.includes(option.value) ? 'bg-blue-100' : ''}`}
              onClick={() => toggleOption(option)}
            >
              <input 
                type="checkbox" 
                checked={selectedValues.includes(option.value)} 
                onChange={() => {}} 
                className="mr-2"
              />
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Fixed FloatingBubbles component with stable positioning
const FloatingBubbles = ({ options, selectedValues, onToggle }) => {
  // Create a simple hash function for consistent "random" values
  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };

  return (
    <div className="min-h-[300px] w-full relative border border-gray-300 rounded p-2 overflow-hidden bg-gradient-to-b from-blue-50 to-blue-100">
      {options.map((option, index) => {
        const columns = 5;
        const column = index % columns;
        const columnWidth = 100 / columns;
        
        // Use hash of option name for consistent positioning
        const hash = hashCode(option);
        const baseX = (column * columnWidth) + (columnWidth * 0.1) + ((hash % 50) / 100 * columnWidth * 0.5);
        
        const randomDuration = 15 + ((hash % 100) / 10); 
        const randomDelay = -((hash % 150) / 10); 
        const randomSize = 70 + ((hash % 300) / 10); 
        
        const wobbleAmount = 20 + (index % 3) * 10;
        
        return (
          <div
            key={`${option}-${index}`} // More stable key
            className={`absolute rounded-full flex items-center justify-center text-xs font-medium cursor-pointer select-none bubble-float p-1 text-center
              ${selectedValues.includes(option) ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border border-gray-300'}`}
            style={{
              left: `${baseX}%`,
              bottom: `-50px`,
              width: `${randomSize}px`,
              height: `${randomSize}px`,
              animationDuration: `${randomDuration}s`,
              animationDelay: `${randomDelay}s`,
              zIndex: selectedValues.includes(option) ? 10 : 1,
              '--wobble-amount': `${wobbleAmount}px`
            }}
            onClick={() => onToggle(option)}
          >
            {option}
          </div>
        );
      })}
      
      <style jsx="true">{`
        @keyframes float-up {
          0% { 
            transform: translate(0, 0) rotate(0deg);
            bottom: -50px;
            opacity: 0.7;
          }
          10% {
            opacity: 1;
          }
          100% { 
            transform: translate(calc(sin(var(--wobble-amount)) * 1deg), -350px) rotate(360deg);
            bottom: 120%;
            opacity: 0.7;
          }
        }
        
        .bubble-float {
          animation-name: float-up;
          animation-iteration-count: infinite;
          animation-timing-function: ease-in-out;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: all 0.2s ease;
          opacity: 0.9;
        }
        
        .bubble-float:hover {
          transform: scale(1.1);
          z-index: 20;
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

// Password input component with visibility toggle
const PasswordInput = ({ id, name, value, onChange, placeholder, error, required = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  return (
    <div className="relative">
      <input
        type={showPassword ? "text" : "password"}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`shadow-sm border ${error ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-10`}
        aria-required={required}
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
        onClick={togglePasswordVisibility}
        aria-label={showPassword ? "Hide password" : "Show password"}
      >
        {showPassword ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
            <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
          </svg>
        )}
      </button>
    </div>
  );
};

// Helper component for tooltips
const InfoTooltip = ({ text }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  return (
    <div className="relative inline-block ml-1">
      <button
        type="button"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        className="text-gray-400 hover:text-gray-600 focus:outline-none"
        aria-label="More information"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      </button>
      
      {isVisible && (
        <div className="absolute z-10 w-64 px-4 py-2 text-sm text-left bg-white border border-gray-200 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2">
          <div className="relative">
            <p className="text-gray-700">{text}</p>
            <div className="absolute w-3 h-3 bg-white border-b border-r border-gray-200 transform rotate-45 -bottom-1.5 left-1/2 -translate-x-1/2"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Password strength meter component
const PasswordStrengthMeter = ({ password }) => {
  const calculateStrength = (pwd) => {
    if (!pwd) return 0;
    
    let strength = 0;
    
    // Length check
    if (pwd.length >= 8) strength += 1;
    
    // Character variety checks
    if (/[A-Z]/.test(pwd)) strength += 1;
    if (/[a-z]/.test(pwd)) strength += 1;
    if (/[0-9]/.test(pwd)) strength += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) strength += 1;
    
    return strength;
  };
  
  const strength = calculateStrength(password);
  
  const getColor = () => {
    switch (strength) {
      case 0: case 1: return 'bg-red-500';
      case 2: case 3: return 'bg-yellow-500';
      case 4: case 5: return 'bg-green-500';
      default: return 'bg-gray-200';
    }
  };
  
  const getLabel = () => {
    switch (strength) {
      case 0: case 1: return 'Weak';
      case 2: case 3: return 'Moderate';
      case 4: case 5: return 'Strong';
      default: return '';
    }
  };
  
  return (
    <div className="mt-1">
      <div className="flex items-center">
        <div className="w-full h-2 bg-gray-200 rounded-full">
          <div 
            className={`h-full rounded-full transition-all duration-300 ${getColor()}`} 
            style={{ width: `${(strength / 5) * 100}%` }}
          ></div>
        </div>
        <span className="ml-2 text-xs whitespace-nowrap">{getLabel()}</span>
      </div>
      <ul className="mt-1 text-xs text-gray-500">
        <li className={`flex items-center ${password && password.length >= 8 ? 'text-green-600' : ''}`}>
          <span className={`inline-block w-3 h-3 mr-1 rounded-full ${password && password.length >= 8 ? 'bg-green-600' : 'bg-gray-300'}`}></span>
          At least 8 characters
        </li>
        <li className={`flex items-center ${/[A-Z]/.test(password) && /[a-z]/.test(password) ? 'text-green-600' : ''}`}>
          <span className={`inline-block w-3 h-3 mr-1 rounded-full ${/[A-Z]/.test(password) && /[a-z]/.test(password) ? 'bg-green-600' : 'bg-gray-300'}`}></span>
          Mix of uppercase and lowercase
        </li>
        <li className={`flex items-center ${/[0-9]/.test(password) ? 'text-green-600' : ''}`}>
          <span className={`inline-block w-3 h-3 mr-1 rounded-full ${/[0-9]/.test(password) ? 'bg-green-600' : 'bg-gray-300'}`}></span>
          At least one number
        </li>
      </ul>
    </div>
  );
};

// Navigation button for the multi-step form
const FormNavButton = ({ onClick, disabled, children, type = "button", primary = false }) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        primary 
          ? `${disabled ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white` 
          : 'bg-white border border-gray-300 hover:bg-gray-100 text-gray-700'
      } font-medium py-3 px-6 rounded-lg shadow-sm focus:outline-none focus:ring-2 ${primary ? 'focus:ring-blue-500' : 'focus:ring-gray-300'} transition-colors duration-200`}
    >
      {children}
    </button>
  );
};

// Progress indicator for multi-step form
const StepIndicator = ({ totalSteps, currentStep, labels, onStepClick }) => {
  return (
    <div className="w-full mb-8">
      <div className="relative flex justify-between">
        {/* Progress bar background */}
        <div className="absolute top-1/2 w-full h-1 bg-gray-200 -translate-y-1/2"></div>
        
        {/* Progress bar fill */}
        <div className="absolute top-1/2 h-1 bg-blue-500 -translate-y-1/2 transition-all duration-300" 
             style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}></div>
        
        {/* Step circles - now clickable */}
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div 
            key={index} 
            className="relative z-10 flex flex-col items-center"
          >
            <button
              onClick={() => onStepClick(index + 1)}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                index + 1 < currentStep 
                  ? 'bg-blue-500 text-white hover:bg-blue-600' 
                  : index + 1 === currentStep 
                  ? 'bg-white border-2 border-blue-500 text-blue-500 hover:bg-blue-50' 
                  : 'bg-white border-2 border-gray-300 text-gray-400 hover:border-blue-300 hover:text-blue-400'
              } focus:outline-none focus:ring-2 focus:ring-blue-300`}
            >
              {index + 1 < currentStep ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                index + 1
              )}
            </button>
            <span 
              className={`mt-2 text-xs font-medium ${
                index + 1 <= currentStep ? 'text-blue-600' : 'text-gray-400'
              } cursor-pointer`}
              onClick={() => onStepClick(index + 1)}
            >
              {labels[index]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionNavigation = ({ currentStep, stepLabels, onStepClick }) => (
  <div className="mt-8 pt-6 border-t border-gray-200">
    <h3 className="text-sm font-medium text-gray-500 mb-3">Jump to Section:</h3>
    <div className="flex flex-wrap gap-2">
      {stepLabels.map((label, index) => (
        <button
          key={index}
          onClick={() => onStepClick(index + 1)}
          className={`px-4 py-2 rounded-lg text-sm transition-colors ${
            currentStep === index + 1
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {index + 1}. {label}
        </button>
      ))}
    </div>
  </div>
);

const completedSchoolingOptions = [
    { value: "N/A", label: "N/A" },
    { value: "High School Diploma", label: "High School Diploma" },
    { value: "Associate's Degree", label: "Associate's Degree" },
    { value: "Bachelor of Science", label: "Bachelor of Science (B.S.)" },
    { value: "Bachelor of Arts", label: "Bachelor of Arts (B.A.)" },
    { value: "Bachelor of Fine Arts", label: "Bachelor of Fine Arts (B.F.A.)" },
    { value: "Master of Science", label: "Master of Science (M.S.)" },
    { value: "Master of Arts", label: "Master of Arts (M.A.)" },
    { value: "Master of Business Administration", label: "Master of Business Administration (M.B.A.)" },
    { value: "Master of Public Health", label: "Master of Public Health (M.P.H.)" },
    { value: "Doctor of Medicine", label: "Doctor of Medicine (M.D.)" },
    { value: "Doctor of Philosophy", label: "Doctor of Philosophy (Ph.D.)" },
    { value: "Doctor of Pharmacy", label: "Doctor of Pharmacy (Pharm.D.)" },
    { value: "Doctor of Dental Surgery", label: "Doctor of Dental Surgery (D.D.S.)" },
    { value: "Juris Doctor", label: "Juris Doctor (J.D.)" }
  ];

// The main enhanced RegistrationForm component
const RegistrationForm = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const { auth, user, register, logout,
    loading: firebaseLoading, firebaseReady, error: firebaseError } = useFirebase();
  const { profile, profileStatus, completeProfile } = useUser();
  const completingProfile = !!user && !user.isAnonymous;
  const hydratedUid = useRef(null);
  const submitting = useRef(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    username: '',
    password: '',
    confirmPassword: '',
    practitionerType: '',
    completedSchooling: [],
    currentSchooling: '',
    areasOfInterest: [],
    currentLocation: '',
  });

  // Multi-step form state
  const [selectedStep, setCurrentStep] = useState(1);
  const currentStep = completingProfile ? Math.max(2, selectedStep) : selectedStep;
  const totalSteps = 4;
  const stepLabels = ['Account', 'Profile', 'Interests', 'Location'];

  const handleStepClick = stepNumber => {
    if (isLoading) return;
    if (stepNumber < currentStep && (!completingProfile || stepNumber >= 2)) setCurrentStep(stepNumber);
    else if (stepNumber === currentStep + 1) goToNextStep();
    window.scrollTo(0, 0);
  };

  const [selectedDate, setSelectedDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [registerError, setRegisterError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (firebaseLoading || isLoading || submitting.current || profileStatus === 'loading' || profileStatus === 'error') return;
    if (completingProfile && profileStatus === 'ready') {
      navigate('/app/profile', { replace: true });
      return;
    }
    if (completingProfile && hydratedUid.current !== user.uid) {
      const switchedAccount = hydratedUid.current && hydratedUid.current !== user.uid;
      hydratedUid.current = user.uid;
      const names = (user.displayName || '').split(' ');
      setFormData(previous => {
        const prev = switchedAccount ? { firstName: '', lastName: '', dateOfBirth: '', practitionerType: '',
          completedSchooling: [], currentSchooling: '', areasOfInterest: [], currentLocation: '' } : previous;
        return {
        ...prev,
        firstName: profile?.firstName || prev.firstName || names[0] || '',
        lastName: profile?.lastName || prev.lastName || names.slice(1).join(' '),
        username: user.email || '', password: '', confirmPassword: '',
        dateOfBirth: profile?.dateOfBirth || prev.dateOfBirth,
        practitionerType: profile?.role || profile?.practitionerType || prev.practitionerType,
        currentSchooling: profile?.currentSchooling || prev.currentSchooling,
        currentLocation: profile?.currentLocation || prev.currentLocation,
        completedSchooling: Array.isArray(profile?.completedSchooling) ? profile.completedSchooling :
          profile?.completedSchooling ? profile.completedSchooling.split(', ').map(label =>
            completedSchoolingOptions.find(option => option.label === label)?.value || label) : prev.completedSchooling,
        areasOfInterest: Array.isArray(profile?.areasOfInterest) ? profile.areasOfInterest :
          profile?.areasOfInterest && profile.areasOfInterest !== 'None specified' ? profile.areasOfInterest.split(', ') : prev.areasOfInterest
        };
      });
      if (profile?.dateOfBirth) setSelectedDate(new Date(profile.dateOfBirth + 'T12:00:00'));
      else if (switchedAccount) setSelectedDate(null);
      if (switchedAccount) {
        setSelectedLocation(null); setManualState(''); setManualCounty(''); setCurrentStep(2);
      }
      setCurrentStep(step => Math.max(2, step));
    } else if (!completingProfile && hydratedUid.current) {
      // Another tab signed out or changed accounts: discard the previous account's form.
      hydratedUid.current = null;
      setFormData({ firstName: '', lastName: '', dateOfBirth: '', username: '', password: '', confirmPassword: '',
        practitionerType: '', completedSchooling: [], currentSchooling: '', areasOfInterest: [], currentLocation: '' });
      setSelectedDate(null);
      setCurrentStep(1);
    }
  }, [firebaseLoading, isLoading, completingProfile, user, profile, profileStatus, navigate]);

  // Add these new state variables here
  const [currentField, setCurrentField] = useState('');
  const [customInterest, setCustomInterest] = useState('');
  
  // Map-related state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapKey, setMapKey] = useState(Date.now());

  // Manual location selection state
  const [manualState, setManualState] = useState('');
  const [manualCounty, setManualCounty] = useState('');
  const [useManualLocation, setUseManualLocation] = useState(false);
  
  // Collapsible sections state
  const [isInterestSectionExpanded, setIsInterestSectionExpanded] = useState(true);
  const [isLocationSectionExpanded, setIsLocationSectionExpanded] = useState(true);
  
  // Responsive state
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  
  // Animation keyframes for background gradient
  const backgroundAnimation = `
    @keyframes gradientAnimation {
      0% { background-position: 0% 50% }
      50% { background-position: 100% 50% }
      100% { background-position: 0% 50% }
    }
  `;
  
  // Animation for floating elements
  const floatingAnimation = `
    @keyframes float {
      0% { transform: translateY(0px) rotate(0deg) }
      50% { transform: translateY(-10px) rotate(3deg) }
      100% { transform: translateY(0px) rotate(0deg) }
    }
  `;
  
  // Check screen size on component mount and window resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);
  
  // Define options for dropdowns (keeping the original options)

  const fieldOptions = [
    { value: "", label: "Select your field" },
    { value: "medicine", label: "Medicine" },
    { value: "dentistry", label: "Dentistry" },
    { value: "pharmacy", label: "Pharmacy" },
    { value: "nursing", label: "Nursing" },
    { value: "student", label: "Pre-health Student" },
    { value: "other", label: "Other Healthcare Field" }
  ];



  const currentSchoolingOptions = [
    { value: "N/A", label: "N/A" },
    { value: "High School", label: "High School" },
    { value: "Undergraduate", label: "Undergraduate" },
    { value: "Master's Program", label: "Master's Program" },
    { value: "Medical School", label: "Medical School" },
    { value: "Fellowship", label: "Fellowship" },
    { value: "Dental School", label: "Dental School" },
    { value: "Pharmacy School", label: "Pharmacy School" },
    { value: "Law School", label: "Law School" },
    { value: "PhD Program", label: "PhD Program" },
    { value: "Post-Doctoral", label: "Post-Doctoral" }
  ];

  // Define specialized interest options for each field
const interestsByField = {
  medicine: [
    "Anesthesiology", "Cardiology", "Dermatology", "Emergency Medicine", 
    "Family Medicine", "Gastroenterology", "Internal Medicine", 
    "Neurology", "Obstetrics & Gynecology", "Oncology", "Ophthalmology",
    "Orthopedic Surgery", "Otolaryngology", "Pediatrics", "Psychiatry", 
    "Radiology", "Urology", "Nephrology", "Endocrinology", 
    "Pulmonology", "Rheumatology", "Infectious Disease", "Pathology"
  ],
  dentistry: [
    "General Dentistry", "Endodontics", "Oral Surgery", "Implantology", 
    "Periodontics", "Prosthodontics", "Orthodontics", "Pediatric Dentistry",
    "Dental Public Health", "Oral Pathology", "Maxillofacial Surgery"
  ],
  pharmacy: [
    "Clinical Pharmacy", "Compounding", "Hospital Pharmacy", 
    "Pharmaceutical Research", "Retail Pharmacy", "Pharmacology",
    "Pharmacognosy", "Pharmacoeconomics", "Regulatory Affairs"
  ],
  nursing: [
    "Critical Care", "Emergency", "Geriatric", "Mental Health", 
    "Midwifery", "Oncology", "Pediatric", "Public Health",
    "Surgical", "Nurse Practitioner", "Nursing Education"
  ],
  student: [
    "Pre-Med", "Pre-Dental", "Pre-Pharmacy", "Pre-Nursing",
    "Research", "Clinical Volunteering", "MCAT/DAT Prep",
    "Application Process", "Interview Preparation"
  ],
  other: [
    "Physical Therapy", "Occupational Therapy", "Speech Therapy",
    "Healthcare Administration", "Medical Technology", "Radiography",
    "Public Health", "Nutrition", "Healthcare Informatics"
  ]
};

// Default to showing all categories if no field is selected
const defaultInterestOptions = [
  "Anesthesiology", "Cardiology", "Dermatology", "Emergency Medicine", "Endodontics", 
  "Family Medicine", "Gastroenterology", "General Dentistry", "General Surgery", "Implantology", 
  "Internal Medicine", "Neurology", "Obstetrics & Gynecology", "Oncology", "Ophthalmology", 
  "Oral Surgery", "Orthopedic Surgery", "Otolaryngology", "Pediatrics", "Periodontics", 
  "Prosthodontics", "Psychiatry", "Radiology", "Urology", "Nephrology", "Endocrinology", 
  "Pulmonology", "Rheumatology", "Infectious Disease", "Pathology", "Pharmacology",
  "Physical Medicine & Rehabilitation", "Plastic Surgery", "Preventive Medicine", 
  "Sports Medicine", "Vascular Surgery", "Critical Care Medicine"
];

// Original areasOfInterestOptions constant (keep this for backward compatibility)
const areasOfInterestOptions = [
  "Anesthesiology", "Cardiology", "Dermatology", "Emergency Medicine", "Endodontics", 
  "Family Medicine", "Gastroenterology", "General Dentistry", "General Surgery", "Implantology", 
  "Internal Medicine", "Neurology", "Obstetrics & Gynecology", "Oncology", "Ophthalmology", 
  "Oral Surgery", "Orthopedic Surgery", "Otolaryngology", "Pediatrics", "Periodontics", 
  "Prosthodontics", "Psychiatry", "Radiology", "Urology", "Nephrology", "Endocrinology", 
  "Pulmonology", "Rheumatology", "Infectious Disease", "Pathology", "Pharmacology",
  "Physical Medicine & Rehabilitation", "Plastic Surgery", "Preventive Medicine", 
  "Sports Medicine", "Vascular Surgery", "Critical Care Medicine"
];

// Add state options for manual selection
const stateOptions = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas',
  'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia'
];
  
  // Force map refresh function
  const refreshMap = () => {
    setMapKey(Date.now());
  };
  
  // Effect to handle map resize when the location section is expanded
  useEffect(() => {
    if (isLocationSectionExpanded) {
      setTimeout(() => {
        setMapKey(Date.now());
      }, 350);
    }
  }, [isLocationSectionExpanded]);

  // Keep all original event handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    if (registerError) setRegisterError('');
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      const formattedDate = date.toISOString().split('T')[0];
      setFormData(prevData => ({
        ...prevData,
        dateOfBirth: formattedDate
      }));
      if (errors.dateOfBirth) {
        setErrors(prevErrors => ({
          ...prevErrors,
          dateOfBirth: ''
        }));
      }
    }
  };

  // Add the new handlers here
const handleCurrentFieldChange = (e) => {
  setCurrentField(e.target.value);
  // When field changes, clear previously selected interests if needed
  // Uncomment the line below if you want to reset interests when field changes
  // setFormData(prevData => ({ ...prevData, areasOfInterest: [] }));
};

const addCustomInterest = () => {
  if (customInterest.trim() === '') return;
  
  // Add the custom interest to areasOfInterest array if it's not already included
  if (!formData.areasOfInterest.includes(customInterest.trim())) {
    setFormData(prevData => ({
      ...prevData,
      areasOfInterest: [...prevData.areasOfInterest, customInterest.trim()]
    }));
  }
  
  // Clear the input field
  setCustomInterest('');
};
  
  const handleLocationSelect = (location) => {
  // Validate location data
  if (!location || !location.county || !location.state) {
    return;
  }
  
  
  const locationString = `${location.county}, ${location.state}`;
  
  setFormData(prevData => ({
    ...prevData,
    currentLocation: locationString
  }));
  
  if (errors.currentLocation) {
    setErrors(prevErrors => ({
      ...prevErrors,
      currentLocation: ''
    }));
  }
  
};

  // Add manual location selection handlers
  const handleManualStateChange = (e) => {
    const state = e.target.value;
    setManualState(state);
    setManualCounty(''); // Reset county when state changes
    
    if (state && manualCounty) {
      updateLocationFromManual(manualCounty, state);
    }
  };

  const handleManualCountyChange = (e) => {
    const county = e.target.value;
    setManualCounty(county);
    
    if (manualState && county) {
      updateLocationFromManual(county, manualState);
    }
  };

  const updateLocationFromManual = (county, state) => {
    const locationString = `${county}, ${state}`;
    
    setFormData(prevData => ({
      ...prevData,
      currentLocation: locationString
    }));
    
    // Clear map selection when using manual
    setSelectedLocation({
      county: county,
      state: state,
      country: "United States"
    });
    
    if (errors.currentLocation) {
      setErrors(prevErrors => ({
        ...prevErrors,
        currentLocation: ''
      }));
    }
  };

  const toggleLocationMethod = () => {
    setUseManualLocation(!useManualLocation);
    // Clear form data when switching methods
    setFormData(prevData => ({
      ...prevData,
      currentLocation: ''
    }));
    setManualState('');
    setManualCounty('');
    setSelectedLocation(null);
  };
  
  const handleAreaOfInterestToggle = (area) => {
    setFormData(prevData => {
      const currentAreas = [...prevData.areasOfInterest];
      if (currentAreas.includes(area)) {
        return {
          ...prevData,
          areasOfInterest: currentAreas.filter(item => item !== area)
        };
      } else {
        return {
          ...prevData,
          areasOfInterest: [...currentAreas, area]
        };
      }
    });
    
    if (errors.areasOfInterest) {
      setErrors(prevErrors => ({
        ...prevErrors,
        areasOfInterest: ''
      }));
    }
  };

  // Add step validation functions
  const validateStep1 = () => {
    const stepErrors = {};
    
    if (!formData.username) stepErrors.username = "Email is required";
    if (formData.username && !/\S+@\S+\.\S+/.test(formData.username)) stepErrors.username = "Email is invalid";
    if (!formData.password) stepErrors.password = "Password is required";
    if (formData.password && formData.password.length < 6) stepErrors.password = "Password must be at least 6 characters";
    if (formData.password !== formData.confirmPassword) stepErrors.confirmPassword = "Passwords do not match";
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };
  
  const validateStep2 = () => {
    const stepErrors = {};
    
    if (!formData.firstName.trim()) stepErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) stepErrors.lastName = "Last name is required";
    if (!formData.dateOfBirth) stepErrors.dateOfBirth = "Date of birth is required";
    if (!formData.practitionerType) stepErrors.practitionerType = "Role is required";
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };
  
  const validateStep3 = () => {
    // No required fields in step 3, but could validate min/max selections if needed
    return true;
  };
  
  const validateStep4 = () => {
    const stepErrors = {};
    
    if (!formData.currentLocation) stepErrors.currentLocation = "Please select a location on the map";
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };
  
  const validateForm = () => {
    const allErrors = {};
    
    // Combine all step validations
    if (!formData.firstName.trim()) allErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) allErrors.lastName = "Last name is required";
    if (!formData.dateOfBirth) allErrors.dateOfBirth = "Date of birth is required";
    if (!completingProfile) {
    if (!formData.username) allErrors.username = "Email is required";
    if (!/\S+@\S+\.\S+/.test(formData.username)) allErrors.username = "Email is invalid";
    if (!formData.password) allErrors.password = "Password is required";
    if (formData.password.length < 6) allErrors.password = "Password must be at least 6 characters";
    if (formData.password !== formData.confirmPassword) allErrors.confirmPassword = "Passwords do not match";
    }
    if (!formData.practitionerType) allErrors.practitionerType = "Role is required";
    if (!formData.currentLocation) allErrors.currentLocation = "Please select a location on the map";
    
    setErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  };

  // Handle step navigation
  const goToNextStep = () => {
    let isValid = false;
    
    switch (currentStep) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      case 4:
        isValid = validateStep4();
        break;
      default:
        isValid = false;
    }
    
    if (isValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  
  const goToPreviousStep = () => {
    if (!isLoading && currentStep > (completingProfile ? 2 : 1)) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

const handleSubmit = async e => {
  e.preventDefault();
  if (submitting.current || isLoading || !firebaseReady) return;
  if (currentStep < totalSteps) { goToNextStep(); return; }
  setRegisterError('');
  if (!validateForm()) {
    setRegisterError('Please complete all required fields in the earlier steps.');
    return;
  }
  submitting.current = true;
  setIsLoading(true);
  let account = user && !user.isAnonymous ? user : null;
  try {
    if (!account) account = (await register(formData.username, formData.password)).user;
    // The account may already exist after a previous failed profile save. Reuse it.
    await completeProfile({
      firstName: formData.firstName.trim(), lastName: formData.lastName.trim(),
      name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
      dateOfBirth: formData.dateOfBirth, role: formData.practitionerType,
      completedSchooling: formData.completedSchooling.length ? formData.completedSchooling.map(val =>
        completedSchoolingOptions.find(opt => opt.value === val)?.label || val).join(', ') : 'N/A',
      currentSchooling: formData.currentSchooling,
      areasOfInterest: formData.areasOfInterest.join(', ') || 'None specified',
      currentLocation: formData.currentLocation,
      ...(selectedLocation ? { locationDetails: selectedLocation } : {})
    }, account);
    showToast('Your profile is ready!', 'success');
    navigate('/app/profile', { replace: true });
  } catch (error) {
    setRegisterError(account && auth.currentUser?.uid === account.uid
      ? 'You are signed in, but your profile could not be saved. Your account is safe. Check your connection and try saving again.'
      : authErrorMessage(error));
  } finally {
    submitting.current = false;
    setIsLoading(false);
  }
};

const handleSignOut = async () => {
  if (isLoading) return;
  setIsLoading(true);
  try { await logout(); navigate('/', { replace: true }); }
  catch (error) { setRegisterError(authErrorMessage(error)); }
  finally { setIsLoading(false); }
};

// Render function for step 1 - Account
const renderStep1 = () => (
  <div className="space-y-6">
    <div>
      <label htmlFor="username" className="block text-gray-700 text-sm font-medium mb-2">
        Email <span className="text-red-500">*</span>
      </label>
      <input
        type="email"
        id="username"
        name="username"
        value={formData.username}
        onChange={handleChange}
        className={`shadow-sm border ${errors.username ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
        aria-required="true"
      />
      {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
      <p className="text-xs text-gray-500 mt-1">We'll never share your email with anyone else.</p>
    </div>
    
    <div>
      <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-2">
        Password <span className="text-red-500">*</span>
        <InfoTooltip text="Create a strong password with at least 8 characters including uppercase, lowercase, and numbers." />
      </label>
      <PasswordInput
        id="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        error={errors.password}
        required={true}
      />
      {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
      <PasswordStrengthMeter password={formData.password} />
    </div>
    
    <div>
      <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-medium mb-2">
        Confirm Password <span className="text-red-500">*</span>
      </label>
      <PasswordInput
        id="confirmPassword"
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={handleChange}
        error={errors.confirmPassword}
        required={true}
      />
      {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
    </div>
    

    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mt-6">
      <p className="text-sm text-blue-800">
        <span className="font-semibold">Privacy Notice:</span> Your information is protected by our privacy policy. We collect this data only to provide you with the best possible user experience.
      </p>
    </div>
  </div>
);
  
  // Render function for step 2 - Profile
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-6 sm:space-y-0">
        <div className="flex-1">
          <label htmlFor="firstName" className="block text-gray-700 text-sm font-medium mb-2">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={`shadow-sm border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
            aria-required="true"
          />
          {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
        </div>
        
        <div className="flex-1">
          <label htmlFor="lastName" className="block text-gray-700 text-sm font-medium mb-2">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={`shadow-sm border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
            aria-required="true"
          />
          {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
        </div>
      </div>
      
      <div>
        <label htmlFor="dateOfBirth" className="block text-gray-700 text-sm font-medium mb-2">
          Date of Birth <span className="text-red-500">*</span>
        </label>
        <DatePicker
          id="dateOfBirth"
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat="yyyy-MM-dd"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          placeholderText="Select your date of birth"
          className={`shadow-sm border ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
          aria-required="true"
        />
        {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>}
      </div>
      
      <div>
        <label htmlFor="practitionerType" className="block text-gray-700 text-sm font-medium mb-2">
          Role <span className="text-red-500">*</span>
          <InfoTooltip text="Select the role that best describes your current position in healthcare education or practice." />
        </label>
        <div className="relative">
          <select
            id="practitionerType"
            name="practitionerType"
            value={formData.practitionerType}
            onChange={handleChange}
            className={`shadow-sm appearance-none border ${errors.practitionerType ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-8`}
            aria-required="true"
          >
            <option value="">Select Role</option>
            <option value="mentor">Mentor</option>
            <option value="undergrad student">Undergrad Student</option>
            <option value="professional student">Professional Student</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
            </svg>
          </div>
        </div>
        {errors.practitionerType && <p className="text-red-500 text-xs mt-1">{errors.practitionerType}</p>}
      </div>
      
      <div>
        <label htmlFor="completedSchooling" className="block text-gray-700 text-sm font-medium mb-2">
          Completed Schooling <span className="text-gray-500 text-xs">(Optional)</span>
        </label>
        <MultiSelectDropdown
          options={completedSchoolingOptions}
          selectedValues={formData.completedSchooling}
          onChange={(selected) => {
            setFormData(prevData => ({
              ...prevData,
              completedSchooling: selected
            }));
          }}
          placeholder="Select degrees completed"
        />
      </div>
      
      <div>
        <label htmlFor="currentSchooling" className="block text-gray-700 text-sm font-medium mb-2">
          Current Schooling <span className="text-gray-500 text-xs">(Optional)</span>
        </label>
        <div className="relative">
          <select
            id="currentSchooling"
            name="currentSchooling"
            value={formData.currentSchooling}
            onChange={handleChange}
            className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-8"
          >
            <option value="">Select Current Schooling</option>
            {currentSchoolingOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );

  // Add the new function for rendering the Areas of Interest section
const renderAreasOfInterestSection = () => (
  <div>
    <div className="flex justify-between items-center mb-2">
      <label htmlFor="currentField" className="block text-gray-700 text-sm font-medium">
        Current Field <span className="text-gray-500 text-xs">(To refine interest options)</span>
        <InfoTooltip text="Select your current field to see more relevant interest options." />
      </label>
    </div>
    
    <div className="relative mb-4">
      <select
        id="currentField"
        name="currentField"
        value={currentField}
        onChange={handleCurrentFieldChange}
        className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-8"
      >
        {fieldOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
        </svg>
      </div>
    </div>
    
    <div className="flex justify-between items-center mb-2">
      <label htmlFor="areasOfInterest" className="block text-gray-700 text-sm font-medium">
        Areas of Interest <span className="text-gray-500 text-xs">(Optional)</span>
        <InfoTooltip text="Select your areas of interest by clicking on the bubbles. This helps us connect you with relevant mentors and resources." />
      </label>
    </div>
    
    <div className="bg-blue-50 p-3 rounded-lg mb-4">
      <span className="text-sm font-medium text-gray-700">Selected: </span>
      <span className="text-sm text-blue-600 font-medium">
        {formData.areasOfInterest.length > 0 
          ? formData.areasOfInterest.join(', ') 
          : 'None selected'}
      </span>
    </div>
    
    {/* Custom interest input */}
    <div className="mb-4 flex">
      <input
        type="text"
        value={customInterest}
        onChange={(e) => setCustomInterest(e.target.value)}
        placeholder="Type a custom interest..."
        className="flex-grow shadow-sm border border-gray-300 rounded-l-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      <button
        type="button"
        onClick={addCustomInterest}
        className="bg-blue-600 text-white px-4 py-2 rounded-r-lg hover:bg-blue-700 transition-colors"
      >
        Add
      </button>
    </div>
    
    <div className="mb-2 text-sm text-gray-500 bg-blue-50 p-2 rounded-lg inline-block">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
      Click on bubbles to select/deselect
    </div>
    
    <FloatingBubbles
      options={currentField ? interestsByField[currentField] || defaultInterestOptions : defaultInterestOptions}
      selectedValues={formData.areasOfInterest}
      onToggle={handleAreaOfInterestToggle}
    />
    
    <p className="text-xs text-gray-500 mt-4">
      This information helps us personalize your experience and connect you with relevant resources, mentors, and opportunities.
    </p>
  </div>
);
  
// Render function for step 3 - Interests
const renderStep3 = () => (
  <div className="space-y-6">
    <div>
      {/* Field selection dropdown */}
      <div className="flex justify-between items-center mb-2">
        <label htmlFor="currentField" className="block text-gray-700 text-sm font-medium">
          Current Field <span className="text-gray-500 text-xs">(To refine interest options)</span>
          <InfoTooltip text="Select your current field to see more relevant interest options." />
        </label>
      </div>
      
      <div className="relative mb-4">
        <select
          id="currentField"
          name="currentField"
          value={currentField}
          onChange={handleCurrentFieldChange}
          className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-8"
        >
          {fieldOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
          </svg>
        </div>
      </div>
      
      {/* Areas of Interest section */}
      <div className="flex justify-between items-center mb-2">
        <label htmlFor="areasOfInterest" className="block text-gray-700 text-sm font-medium">
          Areas of Interest <span className="text-gray-500 text-xs">(Optional)</span>
          <InfoTooltip text="Select your areas of interest by clicking on the bubbles. This helps us connect you with relevant mentors and resources." />
        </label>
      </div>
      
      <div className="bg-blue-50 p-3 rounded-lg mb-4">
        <span className="text-sm font-medium text-gray-700">Selected: </span>
        <span className="text-sm text-blue-600 font-medium">
          {formData.areasOfInterest.length > 0 
            ? formData.areasOfInterest.join(', ') 
            : 'None selected'}
        </span>
      </div>
      
      {/* Custom interest input */}
      <div className="mb-4 flex">
        <input
          type="text"
          value={customInterest}
          onChange={(e) => setCustomInterest(e.target.value)}
          placeholder="Type a custom interest..."
          className="flex-grow shadow-sm border border-gray-300 rounded-l-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={addCustomInterest}
          className="bg-blue-600 text-white px-4 py-2 rounded-r-lg hover:bg-blue-700 transition-colors"
        >
          Add
        </button>
      </div>
      
      <div className="mb-2 text-sm text-gray-500 bg-blue-50 p-2 rounded-lg inline-block">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        Click on bubbles to select/deselect
      </div>
      
      <FloatingBubbles
        options={currentField && interestsByField[currentField] 
          ? interestsByField[currentField] 
          : areasOfInterestOptions}
        selectedValues={formData.areasOfInterest}
        onToggle={handleAreaOfInterestToggle}
      />
      
      <p className="text-xs text-gray-500 mt-4">
        This information helps us personalize your experience and connect you with relevant resources, mentors, and opportunities.
      </p>
    </div>
  </div>
);
  
// Render function for step 4 - Location
const renderStep4 = () => (
  <div className="space-y-6">
    <div>
      <div className="flex justify-between items-center mb-2">
        <label htmlFor="currentLocation" className="block text-gray-700 text-sm font-medium">
          Current Location <span className="text-red-500">*</span>
          <InfoTooltip text="Select your location using the map or enter it manually below." />
        </label>
      </div>
      
      {/* Toggle between map and manual selection */}
      <div className="mb-4 flex items-center justify-center">
        <button
          type="button"
          onClick={toggleLocationMethod}
          className="flex items-center px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Switch to {useManualLocation ? 'Map Selection' : 'Manual Entry'}
        </button>
      </div>
      
      <div className="bg-blue-50 p-3 rounded-lg mb-4">
        {formData.currentLocation ? (
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium text-blue-700">{formData.currentLocation}</span>
          </div>
        ) : (
          <div className="flex items-center text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm">{useManualLocation ? 'Enter your location below' : 'Select a county on the map'}</span>
          </div>
        )}
      </div>
      
      {useManualLocation ? (
        // Manual location selection
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="manualState" className="block text-gray-700 text-sm font-medium mb-2">
                State <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  id="manualState"
                  value={manualState}
                  onChange={handleManualStateChange}
                  className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-8"
                >
                  <option value="">Select State</option>
                  {stateOptions.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                  </svg>
                </div>
              </div>
            </div>
            
            <div>
              <label htmlFor="manualCounty" className="block text-gray-700 text-sm font-medium mb-2">
                County <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="manualCounty"
                value={manualCounty}
                onChange={handleManualCountyChange}
                placeholder="Enter county name"
                className="shadow-sm border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>
      ) : (
        // Map selection (existing code)
        <div className="w-full h-64 sm:h-80 border border-gray-300 rounded-lg shadow-lg overflow-hidden relative">
          <MapContainer 
            key={mapKey}
            center={[39.8, -98.5]}
            zoom={4}
            minZoom={3}
            maxZoom={10}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            whenReady={(mapInstance) => {
              mapInstance.target.invalidateSize();
            }}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
              opacity={0.7}
              maxZoom={18}
            />
            <ZoomControl position="topright" />
            
            <AdminBoundaryMap onCountySelect={handleLocationSelect} />
          </MapContainer>
          
          <style jsx="true">{`
            .county-label {
              background-color: transparent;
              border: none;
              box-shadow: none;
            }
            .county-label div {
              color: #000000;
              font-size: 12px;
              font-weight: bold;
              text-align: center;
              white-space: nowrap;
              text-shadow: -2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff, 2px 2px 0 #fff;
              pointer-events: none;
              user-select: none;
            }
            .county-feature:hover {
              fill-opacity: 0.4 !important;
              cursor: pointer !important;
            }
            .county-popup {
              background-color: rgba(255, 255, 255, 0.9);
              border: 1px solid #0066cc;
              border-radius: 4px;
              padding: 4px 8px;
              font-weight: bold;
            }
            path.leaflet-interactive {
              pointer-events: auto !important;
            }
          `}</style>
        </div>
      )}
      
      {errors.currentLocation && (
        <p className="text-red-500 text-xs mt-1 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {errors.currentLocation}
        </p>
      )}
      
      <p className="text-xs text-gray-500 mt-4">
        This location information will help us connect you with local resources and community opportunities.
      </p>
    </div>
  </div>
);
  
  if (firebaseError || (!isLoading && profileStatus === 'error')) return <AccountRecovery />;
  if (firebaseLoading || (!isLoading && (profileStatus === 'loading' ||
      (completingProfile && hydratedUid.current !== user.uid)))) return <AuthLoading />;

  // Enhanced visually appealing registration form with multi-step wizard
  return (
    <div className={`flex min-h-screen relative overflow-hidden ${isSmallScreen ? 'flex-col' : ''}`}>
      {/* Add animations */}
      <style>
        {backgroundAnimation}
        {floatingAnimation}
        {`
          @media (max-width: 767px) {
            .mobile-hero {
              max-height: 30vh;
            }
          }
          
          .form-container {
            transition: all 0.3s ease-in-out;
          }
          
          .step-enter {
            opacity: 0;
            transform: translateX(50px);
          }
          
          .step-enter-active {
            opacity: 1;
            transform: translateX(0);
            transition: opacity 300ms, transform 300ms;
          }
        `}
      </style>
      
      {/* Floating decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        {[...Array(12)].map((_, i) => (
          <div 
            key={i}
            className="absolute rounded-full bg-blue-500 opacity-5"
            style={{
              width: `${Math.random() * 300 + 50}px`,
              height: `${Math.random() * 300 + 50}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 8 + 12}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>
      
      {/* Left side - healthcare image with enhanced styling */}
      <div 
        className={`${isSmallScreen ? 'w-full mobile-hero' : 'w-1/2'} flex items-center justify-center p-4`}
        style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientAnimation 15s ease infinite'
        }}
      >
        <div className={`${isSmallScreen ? 'h-auto py-6' : 'h-[85vh]'} flex flex-col items-center justify-center relative z-10`}>
          <div className="relative overflow-hidden rounded-2xl shadow-2xl max-w-2xl">
            <img 
              alt="Healthcare professionals" 
              className="w-full h-auto object-cover"
              src="/images/side-view-healthcare-professionals-meeting.jpg"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-800 via-transparent to-transparent opacity-60"></div>
          </div>
          
          <div className="mt-8 text-center text-white">
            <h3 className={`${isSmallScreen ? 'text-2xl' : 'text-3xl'} font-bold mb-4`}>
              Build Your Own Pathway to Practice!
            </h3>
            <p className={`${isSmallScreen ? 'text-lg' : 'text-xl'} opacity-90 max-w-lg`}>
              Join our community of healthcare professionals and chart your course to success.
            </p>
            
            {/* Feature highlights - hide on small screens */}
            {!isSmallScreen && (
              <div className="mt-10 grid grid-cols-2 gap-6 text-left">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1 w-8 h-8 rounded-full bg-white flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    </div>
                  <div>
                    <h4 className="font-semibold">Personalized Dashboard</h4>
                    <p className="text-sm opacity-80">Track your progress and milestones</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1 w-8 h-8 rounded-full bg-white flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                      <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Connect with Mentors</h4>
                    <p className="text-sm opacity-80">Learn from experienced professionals</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1 w-8 h-8 rounded-full bg-white flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Learning Resources</h4>
                    <p className="text-sm opacity-80">Access specialized content</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1 w-8 h-8 rounded-full bg-white flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Career Planning</h4>
                    <p className="text-sm opacity-80">Map your professional journey</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right side - enhanced registration form with multi-step wizard */}
      <div className={`${isSmallScreen ? 'w-full' : 'w-1/2'} flex items-center justify-center bg-white p-4 sm:p-6`}>
        <div className="bg-white p-4 sm:p-8 rounded-2xl shadow-lg w-full max-w-lg relative form-container">
          <div className="absolute top-0 right-0 w-20 h-20 bg-blue-100 rounded-bl-full z-0 opacity-50"></div>
          
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-800 relative z-10">{completingProfile ? 'Complete your profile' : 'Registration'}</h2>
          {completingProfile && <p className="text-sm text-gray-600 mb-4">Signed in as {user.email}</p>}
          
          {/* Step indicator */}
          <StepIndicator 
            totalSteps={totalSteps} 
            currentStep={currentStep} 
            labels={stepLabels} 
            onStepClick={handleStepClick} 
          />
          
          {/* Firebase loading state */}
          {firebaseLoading && (
            <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-4 mb-6 rounded-lg">
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm">Initializing authentication services...</p>
              </div>
            </div>
          )}
          
          {registerError && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-lg" role="alert">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{registerError}</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
            {/* Dynamic step content */}
            <div className="min-h-[320px]">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
            </div>
            
            {/* Navigation buttons */}
            <div className="flex justify-between pt-6">
              {completingProfile && currentStep === 2 ? (
                <FormNavButton onClick={handleSignOut} disabled={isLoading}>Sign out</FormNavButton>
              ) : currentStep === 1 ? (
                <FormNavButton onClick={() => navigate('/')}>
                  Back to Login
                </FormNavButton>
              ) : (
                <FormNavButton onClick={goToPreviousStep}>
                  Previous
                </FormNavButton>
              )}
              
              {currentStep < totalSteps ? (
                <FormNavButton onClick={goToNextStep} disabled={isLoading} primary={true}>
                  Continue
                </FormNavButton>
              ) : (
                <FormNavButton
                  type="submit"
                  disabled={isLoading || !firebaseReady}
                  primary={true}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : completingProfile ? 'Save profile' : 'Register'}
                </FormNavButton>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RegistrationForm;
